<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? "Editar cupón" : "Nuevo cupón" }}</h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 760px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? "Datos del cupón" : "Datos del nuevo cupón" }}</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section">
              <div>
                <h2 class="form-subtitle">Información general</h2>
              </div>

              <div class="row">
                <!-- Código -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="code">Código *</label>
                    <input id="code" type="text" v-model.trim="form.code" autocomplete="off"
                      placeholder="Ej: VERANO25" class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.code.$error }"
                      :disabled="editing"
                      style="text-transform: uppercase" />
                    <FormError v-if="$v.form.code.$error && !$v.form.code.required"
                      message="El código es obligatorio" />
                  </div>
                </div>

                <!-- Descripción -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="description">Descripción</label>
                    <input id="description" type="text" v-model.trim="form.description" autocomplete="off"
                      placeholder="Descripción del cupón" class="custom-form-control" />
                  </div>
                </div>

                <!-- Tipo de descuento -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="discount_type">Tipo de descuento *</label>
                    <FormSelect id="discount_type" v-model="form.discount_type" :items="discountTypes"
                      defaultOption="Elegir tipo de descuento"
                      :showError="$v.form.discount_type.$error && !$v.form.discount_type.required" />
                    <FormError v-if="$v.form.discount_type.$error && !$v.form.discount_type.required"
                      message="El tipo de descuento es obligatorio" />
                  </div>
                </div>

                <!-- Valor -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="discount_value">Valor *</label>
                    <input id="discount_value" type="number" step="0.01" min="0.01"
                      v-model="form.discount_value" autocomplete="off"
                      :placeholder="form.discount_type === 'percent' ? 'Ej: 15 (%)' : 'Ej: 10.00'"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.discount_value.$error }" />
                    <FormError v-if="$v.form.discount_value.$error && !$v.form.discount_value.required"
                      message="El valor es obligatorio" />
                  </div>
                </div>

                <!-- Fecha inicio -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="start_date">Fecha de inicio</label>
                    <input id="start_date" type="date" v-model="form.start_date"
                      class="custom-form-control" />
                  </div>
                </div>

                <!-- Fecha fin -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="end_date">Fecha de fin</label>
                    <input id="end_date" type="date" v-model="form.end_date"
                      class="custom-form-control" />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label for="max_uses">Máximo de usos totales</label>
                    <input id="max_uses" type="number" min="1" v-model="form.max_uses"
                      placeholder="Sin límite" class="custom-form-control" />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-0">
                    <label for="max_uses_per_user">Máximo de usos por pasajero</label>
                    <input id="max_uses_per_user" type="number" min="1"
                      v-model="form.max_uses_per_user"
                      placeholder="Sin límite" class="custom-form-control" />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-group-buttons text-right">
                    <router-link :to="{ name: 'config-coupon-list' }" class="button button-light">
                      {{ editing ? "Volver" : "Cancelar" }}
                    </router-link>
                    <button class="button button-primary" type="submit">Guardar</button>
                  </div>
                </div>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormError } from "wize-admin";
import { required } from "vuelidate/lib/validators";
import { CouponService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

export default {
  components: { FormError, FormSelect },
  data() {
    return {
      discountTypes: LocalConstants.DiscountTypeOptions,
      form: {
        code: null,
        description: null,
        discount_type: null,
        discount_value: null,
        start_date: null,
        end_date: null,
        max_uses: null,
        max_uses_per_user: null,
      },
      editing: false,
    };
  },
  validations() {
    return {
      form: {
        code: { required },
        discount_type: { required },
        discount_value: { required },
      },
    };
  },
  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
  },
  methods: {
    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }

      try {
        this.$store.dispatch("app/loading", true);
        const data = { ...this.form };

        // Convertir valores numéricos
        if (data.discount_value) data.discount_value = parseFloat(data.discount_value);
        if (data.max_uses) data.max_uses = parseInt(data.max_uses);
        else data.max_uses = null;
        if (data.max_uses_per_user) data.max_uses_per_user = parseInt(data.max_uses_per_user);
        else data.max_uses_per_user = null;
        if (!data.start_date) data.start_date = null;
        if (!data.end_date) data.end_date = null;
        if (!data.description) data.description = null;

        if (!this.editing) {
          await CouponService.save(data);
          Alert.success("Cupón creado correctamente");
          this.$router.push({ name: "config-coupon-list" });
        } else {
          const updateData = { ...data };
          delete updateData.code;
          await CouponService.update(this.$route.params.id, updateData);
          Alert.success("Cupón actualizado correctamente");
          this.load(this.$route.params.id);
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors?.message || "Error al guardar el cupón");
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await CouponService.get(id);
        const p = response.payload;
        this.form = {
          code: p.code,
          description: p.description || null,
          discount_type: p.discount_type,
          discount_value: p.discount_value,
          start_date: p.start_date ? p.start_date.substring(0, 10) : null,
          end_date: p.end_date ? p.end_date.substring(0, 10) : null,
          max_uses: p.max_uses || null,
          max_uses_per_user: p.max_uses_per_user || null,
        };
        this.editing = true;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors?.message || "Error al cargar el cupón");
        this.$router.push({ name: "config-coupon-list" });
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },
  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped></style>
